import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';

import { settings as settingsTable } from '@/lib/db';

import { LOGBOOK_OPENING_BALANCE_KEY } from './settings-key';
import { useDraftCount } from './use-draft-count';
import { formatDecimalHours } from './time';

/**
 * "4 entries awaiting approval · Review", wherever a draft-only logbook would
 * otherwise read as empty.
 *
 * Straight after an import every entry is a draft, and Totals, Print and
 * Export all count approved entries only. Each of them said "nothing here"
 * with ten hours of flying one tab away, and none of them said where it was.
 */
export function PendingDrafts({ className = '' }: { className?: string }) {
  const count = useDraftCount();
  if (count === null || count === 0) {
    return null;
  }
  return (
    <p className={`text-muted-foreground text-sm ${className}`} data-testid="pending-drafts">
      {count} {count === 1 ? 'entry' : 'entries'} awaiting approval.{' '}
      <Link to="/logbook" className="text-foreground underline underline-offset-2">
        Review
      </Link>
    </p>
  );
}

/**
 * Hours carried over from the paper logbook, the opening balance every running
 * total starts from.
 *
 * On the Totals tab rather than above the grid: it is set once, and at the top
 * of the entries page it sat between the pilot and the table on every visit.
 */
export function OpeningBalanceField() {
  const openingBalance = useLiveQuery(
    () => settingsTable.get(LOGBOOK_OPENING_BALANCE_KEY, 0),
    [],
    null,
  );
  if (openingBalance === null) {
    return null;
  }
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">Brought forward</span>
      <input
        // Keyed on the stored value so a restore or reset refreshes the field.
        key={openingBalance}
        type="text"
        inputMode="decimal"
        defaultValue={formatDecimalHours(openingBalance)}
        className="border-border bg-surface rounded-control tabular focus-visible:outline-ring w-20 border px-2 py-1 text-right outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
        onBlur={(e) => {
          const parsed = Number(e.target.value.replace(',', '.'));
          void settingsTable.set(
            LOGBOOK_OPENING_BALANCE_KEY,
            Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0,
          );
        }}
      />
      <span className="text-muted-foreground">hr</span>
    </label>
  );
}
